import React, { useState, useEffect, useReducer } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { List, Paper } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import ContactViewListItem from "../ContactViewListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";
import useContactsView from "../../hooks/useContactsView";

import SocketContactsViewList from "../../context/Socket/Listeners/SocketContactsViewList";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		borderRadius: "20px",
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderBottom:"hidden",
	},

	ticketsList: {
		borderRadius:"20px",
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	noTicketsText: {
		borderRadius:"20px",
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		borderRadius:"20px",
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		borderRadius:"20px",
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
}));

// 	***************
// 	** Functions **
// 	***************
const sortContactsView = (contactViewA, contactViewB) => {
	const contactViewALastActivityAt = new Date(contactViewA.lastActivityAt).getTime();
	const contactViewBLastActivityAt = new Date(contactViewB.lastActivityAt).getTime();
	return contactViewBLastActivityAt - contactViewALastActivityAt;
};

const removeNullUndefinedContactsView = (contactView) => {
	return ![null, undefined].includes(contactView);
};

//	**************
// 	** Reducers **
// 	**************
const reducer = (state, action) => {
	if (action.type === "LOAD_CONTACTS_VIEW") {
		const newContactsView = action.payload;

		newContactsView.forEach(contactView => {
			const contactViewIndex = state.findIndex(
				cv => cv.id === contactView.id && cv.whatsappId === contactView.whatsappId
			);

			if (contactViewIndex !== -1) {
				state[contactViewIndex] = contactView;
				if (contactView.unreadMessages > 0) state.push(state.splice(contactViewIndex, 1)[0]);
			}
			else {
				state.push(contactView);
			}
		});

		return [...state];
	}

	if (action.type === "DELETE_CONTACT_VIEW") {
		const { contactView, contactId, whatsappId } = action.payload;
		const contactViewIndex = state.findIndex(cv => cv.id === contactId && cv.whatsappId === whatsappId);
		
		if (contactView) {
			const lastContactLastActivityAt = new Date(state[state.length - 1].lastActivityAt).getTime();
			const contactViewLastActivityAt = new Date(contactView.lastActivityAt).getTime();
			if (lastContactLastActivityAt > contactViewLastActivityAt) return [...state];
	
			if (contactViewIndex === -1) state.push(contactView);
			else state[contactViewIndex] = contactView;
		}
		
		else if (contactViewIndex !== -1) {
			state.splice(contactViewIndex, 1);
		}

		state = state.filter(removeNullUndefinedContactsView);
		state.sort(sortContactsView);
		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const { contactId, whatsappId } = action.payload;
		const contactViewIndex = state.findIndex(cv => cv.id === contactId && cv.whatsappId === whatsappId);

		if (contactViewIndex !== -1) {
			state[contactViewIndex].unreadMessages = 0;
			state[contactViewIndex].isMarkedAsUnread = false;
		}

		return [...state];
	}

	if (action.type === "UPDATE_CONTACT_VIEW") {
		const { contactView, contactId, whatsappId } = action.payload;
		const contactViewIndex = state.findIndex(cv => cv.id === contactId && cv.whatsappId === whatsappId);

		if (contactViewIndex === -1) state.push(contactView);
		else state[contactViewIndex] = contactView;

		state = state.filter(removeNullUndefinedContactsView);
		state.sort(sortContactsView);
		return [...state];
	}
	
	if (action.type === "RESET") { return []; }
};

// 	***************
// 	** Component **
// 	***************
const ContactsViewList = ({ filter, selectedConnectionIds, resetAudioPlayer, style }) => {
	// 	***************
	// 	** Variables **
	// 	***************
	const classes = useStyles();
	const [contactsViewList, dispatch] = useReducer(reducer, []);

	const [pageNumber, setPageNumber] = useState(1);

	const params = {
    pageNumber,
    filter: filter ?? "",
    selectedConnectionIds: selectedConnectionIds ?? [],
  };
		
	const { contactsView, loading, hasMore } = useContactsView(params);


	// 	*****************
	// 	** Use Effects **
	// 	*****************
	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [dispatch, filter, selectedConnectionIds]);

	useEffect(() => {
		dispatch({ type: "LOAD_CONTACTS_VIEW", payload: contactsView });
	}, [contactsView]);



	// 	***************
	// 	** Functions **
	// 	***************
	const handleScroll = (event) => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			event.currentTarget.scrollTop = scrollTop - 100;
			setPageNumber(prevState => prevState + 1);
		}
	};



	// 	************
	//  ** Return **
	// 	************
	return (
    <Paper elevation={0} className={classes.ticketsListWrapper} style={style}>
			<SocketContactsViewList dispatch={dispatch} filter={filter} selectedConnectionIds={selectedConnectionIds} />

			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{!loading && contactsViewList.length === 0
						? (
							<div className={classes.noTicketsDiv}>
								<span className={classes.noTicketsTitle}>{i18n.t("ticketsList.noTicketsTitle")}</span>
								<p className={classes.noTicketsText}>{i18n.t("ticketsList.noTicketsMessage")}</p>
							</div>
						)
						: contactsViewList.map(contactView => (
							<ContactViewListItem contactView={contactView} resetAudioPlayer={resetAudioPlayer} />
						))}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default ContactsViewList;